import {
  Box,
  Button,
  FloatingIndicator,
  Group,
  Modal,
  Paper,
  Stack,
  Tabs,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core'
import type { FileWithPath } from '@mantine/dropzone'
import { notifications } from '@mantine/notifications'
import { IconLink, IconPencil, IconUpload } from '@tabler/icons-react'
import { useCallback, useRef, useState } from 'react'

import { FileUpload, type UploadPhase } from '@/components/FileUpload'
import { theme } from '@/configs/theme'
import { useFileUpload } from '@/hooks/useFileUpload'
import { useGlobalState } from '@/hooks/useGlobalState'
import * as commonClasses from '@/styles/componentCommons.css'
import * as classes from '@/styles/ContentModuleFormScreen.css'
import { toBareS3Key } from '@/utils/uploads'
import { allUploadAccept } from '@/utils/uploadTypes'
import { getUrlLikenessError } from '@/utils/urlValidation'

type ContentTabValue = 'upload' | 'website' | 'manual'

const GI_AI_TEMPLATE_PARAMS = {
  title_sys_prompt_id: 'generate_gi_title',
  title_msg_template_id: 'generate_step_message',
  desc_sys_prompt_id: 'generate_gi_description',
  desc_msg_template_id: 'generate_step_message',
} as const

interface GeneralInfoUploadModalProps {
  opened: boolean
  onClose: () => void
}

export function GeneralInfoUploadModal({ opened, onClose }: GeneralInfoUploadModalProps) {
  const { api } = useGlobalState()
  const [contentTab, setContentTab] = useState<ContentTabValue>('upload')
  const [tabsRootRef, setTabsRootRef] = useState<HTMLDivElement | null>(null)
  const [uploadTabEl, setUploadTabEl] = useState<HTMLButtonElement | null>(null)
  const [websiteTabEl, setWebsiteTabEl] = useState<HTMLButtonElement | null>(null)
  const [manualTabEl, setManualTabEl] = useState<HTMLButtonElement | null>(null)

  const [websiteUrl, setWebsiteUrl] = useState('')
  const [websiteUrlError, setWebsiteUrlError] = useState<string | null>(null)
  const [manualContent, setManualContent] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const [uploadPhase, setUploadPhase] = useState<UploadPhase>('idle')
  const createdRef = useRef(false)

  const {
    uploadProgress,
    upload: uploadFile,
    cancel: cancelUpload,
  } = useFileUpload({
    onPrepareError: () => setUploadPhase('idle'),
    onUploadError: () => setUploadPhase('idle'),
    onUploadCanceled: () => setUploadPhase('idle'),
  })

  const resetState = useCallback(() => {
    setContentTab('upload')
    setWebsiteUrl('')
    setWebsiteUrlError(null)
    setManualContent('')
    setIsCreating(false)
    setUploadPhase('idle')
    createdRef.current = false
  }, [])

  function updateWebsiteUrl(value: string) {
    setWebsiteUrl(value)
    if (websiteUrlError) setWebsiteUrlError(null)
  }

  function handleWebsiteUrlBlur() {
    const likenessError = getUrlLikenessError(websiteUrl)
    if (likenessError) {
      setWebsiteUrlError(likenessError)
    }
  }

  const handleClose = useCallback(() => {
    if (isCreating) {
      cancelUpload()
    }
    resetState()
    onClose()
  }, [isCreating, cancelUpload, resetState, onClose])

  async function createAndClose(params: Parameters<typeof api.createGeneralInfo>[0]) {
    if (createdRef.current) return
    createdRef.current = true
    setIsCreating(true)

    try {
      await api.createGeneralInfo(params)
      api.queryClient.invalidateQueries({ queryKey: ['general-info', 'list'] })
      notifications.show({
        title: 'File Created',
        message: 'The file was successfully created.',
        color: 'green',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
      resetState()
      onClose()
    } catch (_err) {
      createdRef.current = false
      setIsCreating(false)
      notifications.show({
        title: 'Something went wrong',
        message: 'There was an issue creating the file. Please try again.',
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
    }
  }

  async function processDroppedFiles(files: FileWithPath[]) {
    const file = files[0]
    setUploadPhase('uploading')
    const uploadResponse = await uploadFile(file)

    if (!uploadResponse) {
      setUploadPhase('idle')
      return
    }

    setUploadPhase('idle')
    const s3FileKey = toBareS3Key(uploadResponse.filePath)

    await createAndClose({
      file_name: file.name,
      s3_file: s3FileKey,
      ...GI_AI_TEMPLATE_PARAMS,
    })
  }

  async function handleExtractFromWebsite() {
    if (!websiteUrl.trim()) return

    const likenessError = getUrlLikenessError(websiteUrl)
    if (likenessError) {
      setWebsiteUrlError(likenessError)
      return
    }
    setWebsiteUrlError(null)

    await createAndClose({
      url: websiteUrl,
      use_llm_for_scraping: false,
      ...GI_AI_TEMPLATE_PARAMS,
    })
  }

  async function handleAddManualContent() {
    if (!manualContent.trim()) return
    await createAndClose({
      content: manualContent,
      ...GI_AI_TEMPLATE_PARAMS,
    })
  }

  const contentLoading = uploadPhase !== 'idle' || isCreating

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Add File"
      size={theme.rem(1200)}
      centered
      styles={{
        body: { paddingTop: theme.rem(16), backgroundColor: theme.colors.gray[0] },
      }}>
      <Stack bg={theme.colors.gray[0]} gap="xl" pb="xl">
        <Paper shadow="sm" p="md" radius="md">
          <Text size="lg" fw={600} mb="md">
            Add Content
          </Text>

          <Tabs variant="none" value={contentTab} onChange={v => v && setContentTab(v as ContentTabValue)}>
            <Tabs.List ref={setTabsRootRef} className={classes.contentTabsList}>
              <Tabs.Tab
                value="upload"
                ref={setUploadTabEl}
                classNames={classes.contentTabClassNames}
                leftSection={<IconUpload size={18} />}
                disabled={contentLoading}>
                Upload File
              </Tabs.Tab>
              <Tabs.Tab
                value="website"
                ref={setWebsiteTabEl}
                classNames={classes.contentTabClassNames}
                leftSection={<IconLink size={18} />}
                disabled={contentLoading}>
                Provide URL
              </Tabs.Tab>
              <Tabs.Tab
                value="manual"
                ref={setManualTabEl}
                classNames={classes.contentTabClassNames}
                leftSection={<IconPencil size={18} />}
                disabled={contentLoading}>
                Add Manually
              </Tabs.Tab>

              <FloatingIndicator
                target={{ upload: uploadTabEl, website: websiteTabEl, manual: manualTabEl }[contentTab]}
                parent={tabsRootRef}
                className={classes.contentTabIndicator}
              />
            </Tabs.List>

            <Tabs.Panel value="upload">
              <Box className={classes.tabPanelWrapper}>
                <FileUpload
                  activateOnClick={false}
                  loading={contentLoading}
                  disabled={false}
                  uploadPhase={uploadPhase}
                  uploadProgress={uploadProgress}
                  processProgress={0}
                  onDrop={processDroppedFiles}
                  titles={{
                    idle: 'Drag file here to upload',
                  }}
                  EmptyStateProps={{
                    TitleProps: { size: 'lg', fw: 700 },
                    gap: 'xs',
                  }}
                  accept={allUploadAccept}
                  maxFiles={1}
                  primaryActionLabel="Select File"
                  actionButtonProps={{
                    variant: 'outline',
                    color: 'gray',
                    size: 'sm',
                  }}
                  hints={{
                    idle: 'Valid file types: png, jpg, tiff, mp4, mov, mp3, wav, m4a, txt, md, pdf, pptx and docx',
                  }}
                />
              </Box>
            </Tabs.Panel>

            <Tabs.Panel value="website">
              <Box className={classes.tabPanelWrapper}>
                <Stack gap="md">
                  <Stack gap="xs">
                    <Text size="sm">URL</Text>
                    <Group gap="sm" align="stretch">
                      <TextInput
                        placeholder="https://example.com/article"
                        value={websiteUrl}
                        onChange={e => updateWebsiteUrl(e.currentTarget.value)}
                        onBlur={handleWebsiteUrlBlur}
                        size="md"
                        disabled={contentLoading}
                        leftSection={<IconLink size={18} />}
                        error={websiteUrlError}
                        style={{ flex: 1 }}
                        radius="md"
                        variant="filled"
                      />
                      <Button
                        size="md"
                        radius="md"
                        variant="light"
                        onClick={handleExtractFromWebsite}
                        loading={isCreating}
                        disabled={!websiteUrl.trim() || contentLoading}>
                        Add
                      </Button>
                    </Group>
                  </Stack>
                  <Paper shadow="none" p="md" radius="sm" bg="gray.0">
                    <Text size="sm" c="dimmed">
                      Enter a URL for content you want to add. Supports most article pages, blog posts, documentation,
                      and YouTube videos.
                    </Text>
                  </Paper>
                </Stack>
              </Box>
            </Tabs.Panel>

            <Tabs.Panel value="manual">
              <Box className={classes.tabPanelWrapper}>
                <Stack gap="md">
                  <Textarea
                    placeholder="Enter your content here..."
                    value={manualContent}
                    onChange={e => setManualContent(e.currentTarget.value)}
                    minRows={8}
                    autosize
                    disabled={contentLoading}
                  />
                  <Group justify="flex-end">
                    <Button
                      variant="light"
                      onClick={handleAddManualContent}
                      loading={isCreating}
                      disabled={!manualContent.trim() || contentLoading}>
                      Add Content
                    </Button>
                  </Group>
                </Stack>
              </Box>
            </Tabs.Panel>
          </Tabs>

          {contentLoading && (
            <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }} mt="sm">
              <Text c="dimmed" className={classes.inputDescription}>
                {uploadPhase === 'uploading' && 'Uploading file...'}
                {uploadPhase === 'idle' && isCreating && 'Creating content...'}
              </Text>
            </Box>
          )}
        </Paper>

        <Group gap="lg" justify="flex-end">
          <Button variant="light" color="gray" radius="md" onClick={handleClose}>
            Cancel
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
